import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "~/lib/utils";
import { ArtifactRef } from "./artifacts/ArtifactRef";
import { remarkArtifactRefs } from "./remarkArtifactRefs";

interface MarkdownProps {
  text: string;
}

// Lightweight markdown renderer scoped to assistant message bubbles. Pulls
// in nothing from the v1 assistant pipeline; just `react-markdown` + GFM
// plus our `[[name]]` -> <ArtifactRef/> plugin.
export const Markdown = memo(function Markdown({ text }: MarkdownProps) {
  return (
    <div className="text-sm leading-6">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkArtifactRefs]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
});

// `artifact-ref` is emitted by remarkArtifactRefs; react-markdown's
// `components` map keys aren't typed for custom elements, so we cast.
const components = {
  "artifact-ref": ({ name }: { name: string }) => <ArtifactRef name={name} />,
  p: ({ className, ...props }) => (
    <p className={cn("my-3 first:mt-0 last:mb-0", className)} {...props} />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn("font-medium text-primary underline underline-offset-4", className)}
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn("my-3 ml-5 list-disc first:mt-0 last:mb-0 [&>li]:mt-1", className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn("my-3 ml-5 list-decimal first:mt-0 last:mb-0 [&>li]:mt-1", className)}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn("my-3 border-l-2 border-border pl-3 italic first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn("my-4 border-border", className)} {...props} />
  ),
  h1: ({ className, ...props }) => (
    <h1 className={cn("mt-4 mb-2 text-base font-semibold first:mt-0", className)} {...props} />
  ),
  h2: ({ className, ...props }) => (
    <h2 className={cn("mt-4 mb-2 text-base font-semibold first:mt-0", className)} {...props} />
  ),
  h3: ({ className, ...props }) => (
    <h3 className={cn("mt-3 mb-1 text-sm font-semibold first:mt-0", className)} {...props} />
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = typeof className === "string" && className.includes("language-");
    if (isBlock) {
      return (
        <code className={cn("font-mono text-[0.875em]", className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className={cn(
          "rounded border border-border bg-background/60 px-1 py-0.5 font-mono text-[0.875em]",
          className
        )}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "my-3 overflow-x-auto rounded-md border border-border bg-background/60 p-3 text-[0.875em] first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  table: ({ className, ...props }) => (
    <div className="my-3 overflow-x-auto first:mt-0 last:mb-0">
      <table
        className={cn("w-full border-collapse text-left text-[0.95em]", className)}
        {...props}
      />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th className={cn("border-b border-border px-2 py-1 font-semibold", className)} {...props} />
  ),
  td: ({ className, ...props }) => (
    <td className={cn("border-b border-border/60 px-2 py-1", className)} {...props} />
  ),
} as Parameters<typeof ReactMarkdown>[0]["components"];
